import React, { useRef, useState } from "react";

import { MoneyInput } from "./MoneyInput";
import type { RangeValue } from "./RangeSlider";
import { RangeSlider } from "./RangeSlider";

interface RangeControlProps {
  value: RangeValue;
  onChange: (next: RangeValue) => void;
  rangeMin: number;
  rangeMax: number;
  step: number;
  /** Pins the low end and shows a single "Up to" field. */
  singleKnob?: boolean;
  /** Names the values for screen readers, e.g. "price". */
  subject: string;
  /** Distribution bars above the track. Omit for a plain slider. */
  histogramBuckets?: number[];
  histogramHeightPx?: number;
  /**
   * Replaces the min/max fields with a single centered read-out, which is how
   * the All filters modal renders a range. The Price chip's popup keeps the
   * fields, since a popup has no room for the histogram this pairs with.
   *
   * A formatter rather than a string so the read-out can follow the knob
   * mid-drag, while the value itself is still held back until release.
   */
  formatSummary?: (value: RangeValue) => string;
}

/**
 * Slider plus either money fields or a centered read-out, shared by the Price
 * chip's popup and the range sections in the All filters modal.
 */
export const RangeControl: React.FC<RangeControlProps> = ({
  value,
  onChange,
  rangeMin,
  rangeMax,
  step,
  singleKnob = false,
  subject,
  histogramBuckets,
  histogramHeightPx,
  formatSummary,
}) => {
  /*
   * A drag is held here and lifted on release. Publishing every notch made
   * the page re-render — and with it the whole results grid — on each pointer
   * move, which is what made dragging feel heavy. The knob, the histogram
   * shading and the read-out all follow the draft, so the control still
   * tracks the pointer; only the results wait.
   */
  const [draft, setDraft] = useState<RangeValue | null>(null);
  // Mirrored, because the release handler must read the last move's value
  // rather than whatever the closure captured at its render.
  const draftRef = useRef<RangeValue | null>(null);

  const displayValue = draft ?? value;

  const handleDraftChange = (next: RangeValue) => {
    draftRef.current = next;
    setDraft(next);
  };

  const handleCommit = () => {
    const pending = draftRef.current;
    draftRef.current = null;
    setDraft(null);
    if (pending !== null) {
      onChange(pending);
    }
  };

  return (
    <div className="flex flex-col gap-(--slate-dimensions-1)">
      {/* Room below for the knob's overhang and its drop shadow, which reach
          further than the 8px above the bars. */}
      <div className="pt-(--slate-dimensions-2) pb-(--slate-dimensions-4)">
        <RangeSlider
          dualKnob={!singleKnob}
          histogramBuckets={histogramBuckets}
          histogramHeightPx={histogramHeightPx}
          maxKnobLabel={`Maximum ${subject}`}
          minKnobLabel={`Minimum ${subject}`}
          onChange={handleDraftChange}
          onCommit={handleCommit}
          rangeMax={rangeMax}
          rangeMin={rangeMin}
          step={step}
          value={displayValue}
        />
      </div>

      {formatSummary === undefined ? null : (
        <p className="type-fs-subheading text-fs-text-primary text-center">
          {formatSummary(displayValue)}
        </p>
      )}

      {formatSummary !== undefined ? null : (
        <div className="flex items-end gap-(--slate-dimensions-3) pt-(--slate-dimensions-2)">
          {singleKnob ? null : (
            <RangeField
              ariaLabel={`Minimum ${subject}`}
              fieldLabel="Min"
              onCommit={(min) =>
                onChange({ min, max: Math.max(min, displayValue.max) })
              }
              rangeMax={rangeMax}
              rangeMin={rangeMin}
              step={step}
              value={displayValue.min}
            />
          )}
          <RangeField
            ariaLabel={`Maximum ${subject}`}
            fieldLabel={singleKnob ? "Up to" : "Max"}
            onCommit={(max) =>
              onChange({ min: Math.min(displayValue.min, max), max })
            }
            rangeMax={rangeMax}
            rangeMin={rangeMin}
            step={step}
            value={displayValue.max}
          />
          {/* A lone field shouldn't stretch the full width of the section. */}
          {singleKnob ? <div className="flex-1" /> : null}
        </div>
      )}
    </div>
  );
};

const RangeField: React.FC<{
  fieldLabel: string;
  ariaLabel: string;
  value: number;
  rangeMin: number;
  rangeMax: number;
  step: number;
  onCommit: (next: number) => void;
}> = ({ fieldLabel, ariaLabel, value, rangeMin, rangeMax, step, onCommit }) => (
  <label className="flex flex-1 flex-col gap-(--slate-dimensions-1)">
    <span className="type-fs-label text-fs-text-primary">{fieldLabel}</span>
    <MoneyInput
      ariaLabel={ariaLabel}
      fullWidth
      onCommit={onCommit}
      rangeMax={rangeMax}
      rangeMin={rangeMin}
      step={step}
      value={value}
    />
  </label>
);
